import React from 'react';
import { Select } from 'antd';
import moment from 'moment';

import SignUpsPerformance from './SignUpsPerformance';
import SignupImpressions from './SignupImpressions';
import DataTable from '../../../../../../shared/designSystem/components/DataTable';
import AttributionIcon from '../../../../../../shared/designSystem/components/AttributionIcon';
import { attributionWindowSelections } from '../../../../../../../core/utils/constants/constants';
import { MenuItemTextColor } from '../../../../../../shared/globalStyling/styledText';

const { Option } = Select;

// =============================================================================
// SignUpsV2Layout — V2 Sign Ups tab layout.
//
// Stage 1: Performance card at top (KPI + chart + range selector).
// Stage 2: unified Sign Ups section with attribution controls in head +
//          DataTable with manual/server-side pagination.
// =============================================================================

const numericSort = (key) => (rowA, rowB) =>
  (Number(rowA.original[key]) || 0) - (Number(rowB.original[key]) || 0);

const momentSort = (key) => (rowA, rowB) => {
  const a = rowA.original[key];
  const b = rowB.original[key];
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return moment(a).valueOf() - moment(b).valueOf();
};

const idSort = (key) => (rowA, rowB) =>
  String(rowA.original[key] || '').localeCompare(String(rowB.original[key] || ''));

const SignUpsV2Layout = ({
  signupsData,
  signupsLoading,
  signupsByDateData,
  chartLoading,
  onDateRangeChange,
  onAttrMethodChange,
  onAttrWindowChange = () => {},
  onPageChange,
  onPageSizeChange,
  currentPage,
  pageSize,
  dateRange,
  attrMethod,
  attrWindow,
  campaignOrder,
}) => {
  const rows = signupsData?.signups || [];
  const totalSignups = signupsData?.total || 0;

  const columns = [
    {
      id: 'signUpId',
      accessorKey: 'signUpId',
      header: 'Sign Up Id',
      sortable: true,
      sortingFn: idSort('signUpId'),
      meta: { align: 'left', width: 180 },
      cell: ({ getValue }) => (
        <span className="campaign-overview__creatives-name">{getValue() || '—'}</span>
      ),
    },
    {
      id: 'lastImpression',
      accessorKey: 'lastImpression',
      header: 'Last Impression',
      sortable: true,
      sortingFn: momentSort('lastImpression'),
      meta: { align: 'left', width: 180 },
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? moment(v).format('MM/DD/YY HH:mm') : 'N/A';
      },
    },
    {
      id: 'totalImpressions',
      accessorKey: 'totalImpressions',
      header: 'Impressions',
      sortable: true,
      sortingFn: numericSort('totalImpressions'),
      meta: { align: 'right', width: 120 },
      cell: ({ getValue }) => getValue() || 0,
    },
    {
      id: 'signUpDate',
      accessorKey: 'signUpDate',
      header: 'Signed up on',
      sortable: true,
      sortingFn: momentSort('signUpDate'),
      meta: { align: 'left', width: 180 },
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? moment(v).format('MM/DD/YY HH:mm') : '—';
      },
    },
    {
      id: 'attrMethod',
      accessorKey: 'attrMethod',
      header: '',
      sortable: false,
      meta: { align: 'center', width: 70 },
      cell: ({ getValue }) => <AttributionIcon method={getValue()} />,
    },
  ];

  const renderMobileCard = (record) => (
    <>
      <div className="campaign-overview__creatives-card-head">
        <div className="campaign-overview__creatives-card-name">
          <span>{record.signUpId || '—'}</span>
        </div>
        <AttributionIcon method={record.attrMethod} />
      </div>
      <div className="campaign-overview__lineitem-card-meta">
        <span className="campaign-overview__lineitem-flight">
          {record.signUpDate
            ? moment(record.signUpDate).format('MM/DD/YY HH:mm')
            : '—'}
        </span>
      </div>
      <div className="campaign-overview__creatives-card-metrics">
        <div className="campaign-overview__creatives-card-metric">
          <span className="campaign-overview__creatives-card-metric-label">Impressions</span>
          <span className="campaign-overview__creatives-card-metric-value">
            {record.totalImpressions || 0}
          </span>
        </div>
        <div className="campaign-overview__creatives-card-metric">
          <span className="campaign-overview__creatives-card-metric-label">Last impression</span>
          <span
            className="campaign-overview__creatives-card-metric-value"
            style={{ fontSize: 12, fontWeight: 500 }}
          >
            {record.lastImpression
              ? moment(record.lastImpression).format('MM/DD/YY HH:mm')
              : '—'}
          </span>
        </div>
      </div>
    </>
  );

  const handlePaginationChange = ({ pageIndex, pageSize: newPageSize }) => {
    // Controller exposes two separate handlers; preserve which one fires
    // based on whether size changed.
    if (newPageSize !== pageSize) {
      if (onPageSizeChange) onPageSizeChange(1, newPageSize);
    } else {
      if (onPageChange) onPageChange(pageIndex + 1, newPageSize);
    }
  };

  return (
    <div>
      {/* Unified Performance card — KPI + chart + range selector. */}
      <SignUpsPerformance
        startDate={campaignOrder?.startDate}
        endDate={campaignOrder?.endDate}
        totalSignups={totalSignups}
        signupsByDateData={signupsByDateData}
        chartLoading={chartLoading}
        dateRange={dateRange}
        onDateRangeChange={onDateRangeChange}
        campaignOrder={campaignOrder}
      />

      {/* Sign Ups section — attribution controls in head, table below. */}
      <section className="campaign-overview__creatives">
        <header className="campaign-overview__creatives-head">
          <div className="campaign-overview__creatives-eyebrow mono-eyebrow">
            Sign Ups
          </div>
          <div className="campaign-overview__attribution-controls">
            <label className="campaign-overview__attribution-field">
              <span className="campaign-overview__attribution-label mono-eyebrow">
                Method
              </span>
              <Select
                value={attrMethod}
                placeholder="Any"
                onChange={(value) => onAttrMethodChange(value)}
                getPopupContainer={(trigger) => trigger.parentNode}
                popupClassName="mjl-ds"
                className="campaign-overview__attribution-select"
                size="small"
                allowClear
              >
                <Option value="COOKIES">
                  <MenuItemTextColor text="Cookie" />
                </Option>
                <Option value="FINGERPRINT">
                  <MenuItemTextColor text="Fingerprint" />
                </Option>
                <Option value="DEVICE_FINGERPRINT">
                  <MenuItemTextColor text="Device Fingerprint" />
                </Option>
                <Option value="IP">
                  <MenuItemTextColor text="IP Address" />
                </Option>
              </Select>
            </label>

            <label className="campaign-overview__attribution-field">
              <span className="campaign-overview__attribution-label mono-eyebrow">
                Window
              </span>
              <Select
                value={attrWindow}
                onChange={(value) => onAttrWindowChange(value)}
                getPopupContainer={(trigger) => trigger.parentNode}
                popupClassName="mjl-ds"
                className="campaign-overview__attribution-select"
                size="small"
              >
                {attributionWindowSelections.map((option) => (
                  <Option key={option.key} value={option.key}>
                    <MenuItemTextColor text={option.name} />
                  </Option>
                ))}
              </Select>
            </label>
          </div>
        </header>

        {signupsLoading ? (
          <div style={{ padding: 16 }}>
            <span
              className="skeleton"
              style={{ width: '100%', height: 220, borderRadius: 12, display: 'block' }}
            />
          </div>
        ) : (
          // Wrap in `__creatives-body` so the card's 16px border-radius
          // doesn't clip the table's right edge. The body's flex handles
          // padding even when there's no breakdown aside.
          <div className="campaign-overview__creatives-body">
            <div className="campaign-overview__creatives-table">
              <DataTable
              data={rows}
              columns={columns}
              pageSize={pageSize}
              pageSizeOptions={[10, 20, 50, 100]}
              mobileBreakpoint="md"
              renderMobileCard={renderMobileCard}
              manualPagination
              pageIndex={(currentPage || 1) - 1}
              totalRows={totalSignups}
              onPaginationChange={handlePaginationChange}
              expandable={{
                isRowExpandable: () => true,
                renderExpandedRow: (record) => <SignupImpressions signupId={record.id} />,
              }}
              emptyState="No sign ups match the current filter."
            />
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default SignUpsV2Layout;
